import { UnitTour } from '../../types/Unit/Unit';
import { useMemo } from 'react';
import { Option, RangeOption } from '../../types/Option/Option';
import getFilterOptions from '../../utils/getFilterOptions/getFilterOptions';
import Filter from '../../types/Filter/Filter';
import { getLevels } from '../../utils/getLevels/getLevels';

/*
    Creates configuration for units filters, based on values they have for specific
    properties.
    Example: units have 1, 2 or 3 bedrooms. Filters will not have a choice to
    filter by 4 bedrooms because unit with 4 bedrooms doesn't exist in provided data.
 */
export default function useFilters(unitTours: UnitTour[]): Filter[] {
    const floorPlanTypeOptions = useMemo<Option[]>(
        () => getFilterOptions(unitTours, 'floorPlanType'),
        [unitTours]
    );

    const numBedOptions = useMemo<Option[]>(
        () =>
            getFilterOptions(unitTours, 'numBed').map((option) =>
                option.key === 0 ? { ...option, displayName: 'Studio' } : option
            ),
        [unitTours]
    );

    const numBathOptions = useMemo<Option[]>(
        () => getFilterOptions(unitTours, 'numBath'),
        [unitTours]
    );

    const unitSizeOptions = useMemo<RangeOption[]>(() => {
        return getLevels(
            unitTours.map((unitTour) => unitTour.unitSize?.num).filter(Boolean)
        );
    }, [unitTours]);

    return (
        [
            {
                key: 'floorPlanType',
                displayName: 'Floor Plan Type',
                options: floorPlanTypeOptions,
                type: 'value',
            },
            {
                key: 'numBed',
                displayName: 'Bedrooms',
                options: numBedOptions,
                type: 'value',
            },
            {
                key: 'numBath',
                displayName: 'Bathrooms',
                options: numBathOptions,
                type: 'value',
            },
            {
                key: 'unitSize',
                displayName: 'Square Feet',
                options: unitSizeOptions,
                type: 'range',
            },
        ] as Filter[]
    ).filter((filter) => filter.options.length);
}
